import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, Award, Target, History, Save, Loader2, AlertCircle } from "lucide-react";
import { z } from "zod";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { companyProfileService } from "@/services/companyProfile.service";
import { computeProfileCompleteness } from "@/lib/intelligence/company-profile-completeness";
import { Progress } from "@/components/ui/progress";
import { indicadoresService } from "@/services/indicadores.service";
import {
  INDICADORES_FALLBACK,
  MONEDAS_MP,
  equivalencias,
  formatMonto,
  parseMonto,
  type Indicadores,
  type MonedaMP,
} from "@/lib/monto";
import { cn } from "@/lib/utils";
import { cleanRut, esRutValido, formatRut } from "@/lib/rut";
import { TagsField } from "@/components/empresa/TagsField";
import { CatalogoFamiliasField } from "@/components/empresa/CatalogoFamiliasField";
import { useTenant } from "@/hooks/use-tenant";

import type { CompanyProfile } from "@/types";

export const Route = createFileRoute("/empresa")({
  head: () => ({
    meta: [
      { title: "Perfil Empresa — LicitaPro" },
      {
        name: "description",
        content:
          "Company Intelligence Profile: datos, capacidades, factores estratégicos e historial.",
      },
      { property: "og:title", content: "Perfil Empresa — LicitaPro" },
      {
        property: "og:description",
        content: "Configura el ADN comercial que alimenta al motor IA.",
      },
    ],
  }),
  component: EmpresaPage,
});

const tabs = [
  { id: "datos", label: "Datos", icon: Briefcase },
  { id: "capacidades", label: "Capacidades", icon: Award },
  { id: "estrategia", label: "Estrategia", icon: Target },
  { id: "historial", label: "Historial", icon: History },
] as const;
type TabId = (typeof tabs)[number]["id"];

const lista = z.array(z.string());

/**
 * Autocomplete de región compartido entre "Datos → regiones" y "Estrategia →
 * regionesPreferidas": el scoring compara región por igualdad exacta de
 * texto contra Licitacion.region, así que ambos campos deben ofrecer el
 * mismo catálogo real (ver CompanyProfileService.searchRegiones).
 */
const searchRegionesMp = (query: string) =>
  companyProfileService.searchRegiones(query).then((res) => res.map((r) => r.nombre));

const companyProfileSchema = z.object({
  datos: z.object({
    razonSocial: z.string().min(2, "Mínimo 2 caracteres"),
    // Opcional a propósito: la ficha se llena de a poco, no se exige RUT
    // desde el primer guardado. Si se escribe algo, sí debe ser válido.
    rut: z
      .string()
      .refine((v) => v === "" || esRutValido(v), "RUT inválido (verifica el dígito verificador)"),
    rubros: lista,
    servicios: lista,
    productos: lista,
    especialidades: lista,
    categorias: lista,
    keywords: lista,
    regiones: lista,
    // Familias UNSPSC de 4 dígitos que la empresa vende. Se editan con el
    // selector de Compra Ágil, no como texto libre.
    familiasUnspsc: z.array(z.string().regex(/^\d{4}$/)).default([]),
    tamano: z.enum(["micro", "pequena", "mediana", "grande"]),
    aniosExperiencia: z.coerce
      .number({ invalid_type_error: "Debe ser un número" })
      .int("Debe ser un número entero")
      .min(0, "No puede ser negativo")
      .max(150, "Valor no realista"),
  }),
  capacidades: z.object({
    certificaciones: lista,
    tecnologias: lista,
    frameworks: lista,
    herramientas: lista,
    infraestructura: lista,
    personalTecnico: z.coerce
      .number({ invalid_type_error: "Debe ser un número" })
      .int("Debe ser un número entero")
      .min(0, "No puede ser negativo")
      .max(1_000_000, "Valor no realista"),
    maquinaria: lista,
    experienciaSectorial: lista,
  }),
  estrategia: z
    .object({
      presupuestoMin: z.coerce
        .number({ invalid_type_error: "Debe ser un número" })
        .min(0, "No puede ser negativo"),
      presupuestoMax: z.coerce
        .number({ invalid_type_error: "Debe ser un número" })
        .min(0, "No puede ser negativo"),
      presupuestoMoneda: z.enum(["CLP", "UF", "UTM"]),
      regionesPreferidas: lista,
      sectoresPreferidos: lista,
      complejidadAceptada: z.enum(["baja", "media", "alta"]),
      toleranciaRiesgo: z.enum(["baja", "media", "alta"]),
      modalidadesPreferidas: lista,
    })
    .refine((v) => v.presupuestoMax >= v.presupuestoMin, {
      message: "El presupuesto máximo no puede ser menor al mínimo",
      path: ["presupuestoMax"],
    }),
  // historial ya no es editable: se calcula en el servidor desde los
  // movimientos reales (TenantLicitacion) y se muestra solo lectura.
});

type FormValues = z.infer<typeof companyProfileSchema>;

/**
 * Plantilla en blanco para tenants sin perfil guardado aún. A propósito NO
 * contiene datos de ejemplo con apariencia real (ver company-profile.mock.ts):
 * si el usuario guarda sin editar, no queremos que quede una empresa ficticia
 * en su ficha ni que el motor de scoring la use como si fuera real.
 */
const EMPTY_COMPANY_PROFILE: CompanyProfile = {
  id: "",
  datos: {
    razonSocial: "",
    rut: "",
    rubros: [],
    servicios: [],
    productos: [],
    especialidades: [],
    categorias: [],
    keywords: [],
    regiones: [],
    familiasUnspsc: [],
    tamano: "pequena",
    aniosExperiencia: 0,
  },
  capacidades: {
    certificaciones: [],
    tecnologias: [],
    frameworks: [],
    herramientas: [],
    infraestructura: [],
    personalTecnico: 0,
    maquinaria: [],
    experienciaSectorial: [],
  },
  estrategia: {
    presupuestoMin: 0,
    presupuestoMax: 0,
    presupuestoMoneda: "CLP",
    regionesPreferidas: [],
    sectoresPreferidos: [],
    complejidadAceptada: "media",
    toleranciaRiesgo: "media",
    modalidadesPreferidas: [],
  },
  historial: {
    ganadas: 0,
    perdidas: 0,
    postuladasTotales: 0,
    organismosFrecuentes: [],
    categoriasExitosas: [],
    tasaAdjudicacion: 0,
    adjudicacionesPorConfirmar: [],
  },
};

/**
 * `razonSocial` se muestra en el header como campo de solo lectura (viene de
 * TGT One, no lo edita el usuario), pero antes nunca se escribía dentro del
 * form: el schema exige mínimo 2 caracteres y el valor interno quedaba en ""
 * para cualquier perfil nuevo, así que el guardado fallaba en silencio (la
 * pestaña "Datos" se marcaba inválida sin ningún campo visible en rojo).
 */
function toFormValues(p: CompanyProfile, tenantName?: string): FormValues {
  return {
    datos: {
      ...p.datos,
      razonSocial: tenantName || p.datos.razonSocial,
      rut: p.datos.rut ? formatRut(p.datos.rut) : "",
    },
    capacidades: { ...p.capacidades },
    estrategia: { ...p.estrategia, presupuestoMoneda: p.estrategia.presupuestoMoneda ?? "CLP" },
  };
}

function toPayload(v: FormValues): Partial<CompanyProfile> {
  return {
    datos: { ...v.datos, rut: cleanRut(v.datos.rut) },
    capacidades: { ...v.capacidades },
    estrategia: { ...v.estrategia },
  };
}


function EmpresaPage() {
  const [tab, setTab] = useState<TabId>("datos");
  const queryClient = useQueryClient();
  const { tenantName } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["company-profile"],
    queryFn: () => companyProfileService.get(),
  });

  const profileMissing = !isLoading && data === null;
  const profile = data ?? EMPTY_COMPANY_PROFILE;
  const completeness = computeProfileCompleteness(profile);
  // La razón social es identidad del tenant: viene del login central, no del perfil local.
  const razonSocial = tenantName ?? profile.datos.razonSocial;


  const form = useForm<FormValues>({
    resolver: zodResolver(companyProfileSchema),
    values: toFormValues(profile, razonSocial),
    mode: "onBlur",
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => companyProfileService.update(toPayload(v)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-profile"] });
      toast.success("Perfil guardado");
    },
    onError: (err) => {
      toast.error("No se pudo guardar", { description: String(err) });
    },
  });

  const onSubmit = (v: FormValues) => mutation.mutate(v);
  /**
   * Las pestañas se desmontan al cambiar (`tab === "x" && <Tab/>`), así que
   * un error en una pestaña no visible no muestra texto rojo en ningún
   * lado: solo se ve el toast genérico. Para que el usuario no se quede
   * sin pista, saltamos a la primera pestaña con error y las marcamos todas.
   */
  const onInvalid = (errors: FieldErrors<FormValues>) => {
    const erroredTabs = (["datos", "capacidades", "estrategia"] as const).filter(
      (id) => errors[id] && Object.keys(errors[id] as object).length > 0,
    );
    if (erroredTabs.length && !erroredTabs.includes(tab as (typeof erroredTabs)[number])) {
      setTab(erroredTabs[0]);
    }
    toast.error("Revisa los campos marcados en rojo", {
      description: erroredTabs.length
        ? `Hay datos inválidos en: ${erroredTabs
            .map((id) => tabs.find((t) => t.id === id)?.label)
            .join(", ")}.`
        : "Hay datos inválidos que impiden guardar el perfil.",
    });
  };
  const submitForm = form.handleSubmit(onSubmit, onInvalid);

  return (
    <AppShell
      title="Perfil empresa"
      subtitle="ADN comercial que alimenta el motor IA"
      actions={
        <Button
          size="sm"
          onClick={submitForm}
          disabled={mutation.isPending || !form.formState.isDirty}
        >
          {mutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
          Guardar
        </Button>
      }
    >
      <form onSubmit={submitForm}>
        {!isLoading && !completeness.isComplete && (
          <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="size-4 text-warning mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-sm font-medium text-foreground">
                  {profileMissing
                    ? "Aún no has completado el perfil de tu empresa"
                    : "Tu perfil de empresa está incompleto"}
                </p>
                <span className="text-xs font-mono text-muted-foreground shrink-0 tabular-nums">
                  {completeness.completed}/{completeness.total}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Faltan {completeness.total - completeness.completed} de {completeness.total} datos
                que usa el motor de IA para calcular Compatibilidad, Benchmark y el Match Score
                global.
              </p>
              <Progress value={completeness.percent} className="h-1.5" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {completeness.items
                  .filter((i) => !i.done)
                  .map((i) => (
                    <span
                      key={i.key}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                    >
                      {i.label}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-elegant mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="size-14 rounded-xl bg-gradient-primary grid place-items-center text-primary-foreground font-bold text-lg shadow-glow shrink-0">
              {razonSocial.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Razón social</Label>
                <Input value={razonSocial} readOnly disabled className="mt-1" />
                <p className="text-[11px] text-muted-foreground mt-1">Gestionado en TGT One</p>
              </div>
              <div>
                <Label>RUT</Label>
                <Input
                  value={form.watch("datos.rut") ?? ""}
                  onChange={(e) =>
                    form.setValue("datos.rut", formatRut(e.target.value), {
                      shouldDirty: true,
                      shouldValidate: form.formState.isSubmitted,
                    })
                  }
                  onBlur={() => form.trigger("datos.rut")}
                  placeholder="76.123.456-7"
                  inputMode="text"
                  className="mt-1"
                />
                {form.formState.errors.datos?.rut ? (
                  <p className="text-xs text-destructive mt-1">
                    {form.formState.errors.datos.rut.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="hidden md:grid grid-cols-3 gap-4 text-center">
              <Stat
                value={`${Math.round(profile.historial.tasaAdjudicacion * 100)}%`}
                label="Tasa adjudicación"
              />
              <Stat value={profile.historial.ganadas} label="Ganadas" />
              <Stat value={profile.capacidades.personalTecnico} label="Equipo técnico" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-xl border border-border bg-card shadow-elegant">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs.map((t) => {
              const Icon = t.icon;
              const hasError =
                t.id !== "historial" &&
                !!form.formState.errors[t.id as "datos" | "capacidades" | "estrategia"];
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition",
                    tab === t.id
                      ? "border-primary text-primary font-medium"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className="size-4" /> {t.label}
                  {hasError && (
                    <span
                      className="size-1.5 rounded-full bg-destructive"
                      aria-label={`${t.label} tiene datos inválidos`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {tab === "datos" && <DatosTab form={form} />}
            {tab === "capacidades" && <CapacidadesTab form={form} />}
            {tab === "estrategia" && <EstrategiaTab form={form} />}
            {tab === "historial" && <HistorialTab historial={profile.historial} />}
          </div>
        </div>
      </form>
    </AppShell>
  );
}

/**
 * Ayudas de campo: la diferencia entre rubro / servicio / producto /
 * especialidad es la principal fuente de confusión al llenar la ficha, así que
 * cada campo lleva su definición y ejemplos a la vista.
 */
function DatosTab({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const fields: {
    name: keyof FormValues["datos"];
    label: string;
    hint: string;
    help: string;
    placeholder: string;
    remoteSearch?: (query: string) => Promise<string[]>;
  }[] = [
    {
      name: "rubros",
      label: "Rubros",
      hint: "El sector económico en que opera tu empresa.",
      help: "Rubro = a qué industria perteneces (nivel más amplio). Ej: Construcción, Tecnología y Software, Servicios de Aseo, Transporte.",
      placeholder: "Tecnología y Software",
    },
    {
      name: "servicios",
      label: "Servicios",
      hint: "El trabajo que ejecutas para el cliente.",
      help: "Servicio = actividad que realizas y se cobra por ejecución. Ej: Desarrollo de software a medida, Soporte TI 24/7, Mantención de ascensores, Aseo industrial.",
      placeholder: "Soporte TI 24/7",
    },
    {
      name: "productos",
      label: "Productos",
      hint: "Bienes o licencias que entregas.",
      help: "Producto = algo tangible o licenciable que vendes, no una hora de trabajo. Ej: Notebooks, Plataforma de gestión documental (licencia), Mobiliario clínico, Insumos de aseo.",
      placeholder: "Plataforma de gestión documental",
    },
    {
      name: "especialidades",
      label: "Especialidades",
      hint: "Tu nicho o diferenciador frente a la competencia.",
      help: "Especialidad = dónde eres realmente fuerte y te distingue. Ej: Hospitales públicos, Integración con SII, Obras en altura, Ciberseguridad OT.",
      placeholder: "Sector salud público",
    },
    {
      name: "categorias",
      label: "Categorías (Mercado Público)",
      hint: "Categorías UNSPSC en que buscas licitaciones.",
      help: "Se usan para filtrar y puntuar oportunidades. Empieza a escribir y elige de las categorías UNSPSC reales que Mercado Público ya usa en las licitaciones sincronizadas.",
      placeholder: "Consultoría",
      remoteSearch: (query: string) =>
        companyProfileService
          .searchCategoriasUnspsc(query)
          .then((res) => res.map((c) => c.nombre)),
    },
    {
      name: "keywords",
      label: "Keywords",
      hint: "Palabras que aparecen en las bases de las licitaciones que te sirven.",
      help: "El motor IA las busca en el nombre y la descripción. Ej: software, ciberseguridad, mantención preventiva, gestión documental.",
      placeholder: "transformación digital",
    },
    {
      name: "regiones",
      label: "Regiones de operación",
      hint: "Dónde puedes prestar servicio hoy.",
      help: "Regiones con cobertura real (equipo, sucursal o logística). Empieza a escribir y elige de las regiones reales que usa Mercado Público.",
      placeholder: "Metropolitana",
      remoteSearch: searchRegionesMp,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <Label>Tamaño empresa</Label>
          <Select
            value={form.watch("datos.tamano")}
            onValueChange={(v) => form.setValue("datos.tamano", v as FormValues["datos"]["tamano"])}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="micro">Micro</SelectItem>
              <SelectItem value="pequena">Pequeña</SelectItem>
              <SelectItem value="mediana">Mediana</SelectItem>
              <SelectItem value="grande">Grande</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Años de experiencia</Label>
          <Input
            type="number"
            min={0}
            max={150}
            step={1}
            {...form.register("datos.aniosExperiencia")}
            className="mt-1"
          />
          {form.formState.errors.datos?.aniosExperiencia && (
            <p className="text-xs text-destructive mt-1">
              {form.formState.errors.datos.aniosExperiencia.message}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <ListField
            key={f.name}
            name={`datos.${f.name}`}
            label={f.label}
            hint={f.hint}
            help={f.help}
            placeholder={f.placeholder}
            form={form}
            remoteSearch={f.remoteSearch}
          />
        ))}
      </div>

      <div className="rounded-xl border border-border p-4">
        <CatalogoFamiliasField
          value={form.watch("datos.familiasUnspsc") ?? []}
          onChange={(next) =>
            form.setValue("datos.familiasUnspsc", next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </div>
    </div>
  );
}

function CapacidadesTab({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const fields: {
    name: keyof FormValues["capacidades"];
    label: string;
    hint: string;
    placeholder: string;
  }[] = [
    { name: "certificaciones", label: "Certificaciones", hint: "Acreditaciones vigentes.", placeholder: "ISO 9001" },
    { name: "tecnologias", label: "Tecnologías", hint: "Plataformas que dominas.", placeholder: "AWS" },
    { name: "frameworks", label: "Frameworks", hint: "Stack de desarrollo o metodologías.", placeholder: "React" },
    { name: "herramientas", label: "Herramientas", hint: "Software de apoyo a la operación.", placeholder: "Jira" },
    { name: "infraestructura", label: "Infraestructura", hint: "Instalaciones o capacidad instalada.", placeholder: "Cloud" },
    { name: "maquinaria", label: "Maquinaria", hint: "Equipos propios relevantes para ejecutar.", placeholder: "Retroexcavadora" },
    { name: "experienciaSectorial", label: "Experiencia sectorial", hint: "Tipos de organismo donde ya trabajaste.", placeholder: "Municipalidades" },
  ];

  return (
    <div className="space-y-5">
      <div>
        <Label>Personal técnico</Label>
        <Input
          type="number"
          min={0}
          step={1}
          {...form.register("capacidades.personalTecnico")}
          className="mt-1 max-w-xs"
        />
        {form.formState.errors.capacidades?.personalTecnico && (
          <p className="text-xs text-destructive mt-1">
            {form.formState.errors.capacidades.personalTecnico.message}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <ListField
            key={f.name}
            name={`capacidades.${f.name}`}
            label={f.label}
            hint={f.hint}
            placeholder={f.placeholder}
            form={form}
          />
        ))}
      </div>
    </div>
  );
}

/** Input de monto con separador de miles y equivalencias en las otras unidades de MP. */
function MontoField({
  label,
  field,
  form,
  moneda,
  indicadores,
}: {
  label: string;
  field: "estrategia.presupuestoMin" | "estrategia.presupuestoMax";
  form: ReturnType<typeof useForm<FormValues>>;
  moneda: MonedaMP;
  indicadores: Indicadores;
}) {
  const valor = Number(form.watch(field)) || 0;
  const fieldName = field.split(".")[1] as "presupuestoMin" | "presupuestoMax";
  const error = form.formState.errors.estrategia?.[fieldName];
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1">
        <Input
          inputMode="decimal"
          value={valor ? formatMonto(valor, moneda) : ""}
          onChange={(e) =>
            form.setValue(field, parseMonto(e.target.value), { shouldDirty: true, shouldValidate: true })
          }
          className="pr-14"
          placeholder="0"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {moneda}
        </span>
      </div>
      {error ? (
        <p className="text-xs text-destructive mt-1">{error.message}</p>
      ) : (
        <p className="text-xs text-muted-foreground mt-1">
          {equivalencias(valor, moneda, indicadores)}
        </p>
      )}
    </div>
  );
}


function EstrategiaTab({ form }: { form: ReturnType<typeof useForm<FormValues>> }) {
  const selectFields: {
    name: "toleranciaRiesgo" | "complejidadAceptada";
    label: string;
    options: { value: string; label: string }[];
  }[] = [
    {
      name: "toleranciaRiesgo",
      label: "Tolerancia al riesgo",
      options: [
        { value: "baja", label: "Baja" },
        { value: "media", label: "Media" },
        { value: "alta", label: "Alta" },
      ],
    },
    {
      name: "complejidadAceptada",
      label: "Complejidad aceptada",
      options: [
        { value: "baja", label: "Baja" },
        { value: "media", label: "Media" },
        { value: "alta", label: "Alta" },
      ],
    },
  ];

  const moneda = (form.watch("estrategia.presupuestoMoneda") ?? "CLP") as MonedaMP;
  const { data: indicadores = INDICADORES_FALLBACK } = useQuery({
    queryKey: ["indicadores"],
    queryFn: indicadoresService.get,
    staleTime: 6 * 60 * 60 * 1000,
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Label>Moneda del presupuesto</Label>
          <Select
            value={moneda}
            onValueChange={(v) =>
              form.setValue("estrategia.presupuestoMoneda", v as MonedaMP, { shouldDirty: true })
            }
          >
            <SelectTrigger className="mt-1 md:max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONEDAS_MP.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Mercado Público publica montos en CLP, UF y UTM. El motor IA convierte todo a CLP
            {indicadores.fecha ? ` con los valores del ${indicadores.fecha}` : ""} (UF $
            {formatMonto(indicadores.uf, "UF")} · UTM ${formatMonto(indicadores.utm, "CLP")}).
          </p>
        </div>
        <MontoField
          label="Presupuesto mínimo"
          field="estrategia.presupuestoMin"
          form={form}
          moneda={moneda}
          indicadores={indicadores}
        />
        <MontoField
          label="Presupuesto máximo"
          field="estrategia.presupuestoMax"
          form={form}
          moneda={moneda}
          indicadores={indicadores}
        />

        {selectFields.map((f) => (
          <div key={f.name}>
            <Label>{f.label}</Label>
            <Select
              value={((form.watch(f.name as keyof FormValues) as unknown) as string) ?? ""}
              onValueChange={(v) =>
                form.setValue(f.name as keyof FormValues, v as unknown as FormValues[keyof FormValues])
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {f.options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
        <ListField
          name="estrategia.regionesPreferidas"
          label="Regiones preferidas"
          hint="Dónde prefieres ejecutar. Suma puntaje en el match."
          placeholder="Metropolitana"
          form={form}
          remoteSearch={searchRegionesMp}
        />
        <ListField
          name="estrategia.sectoresPreferidos"
          label="Sectores preferidos"
          hint="Sectores donde quieres competir."
          placeholder="Salud"
          form={form}
        />
        <ListField
          name="estrategia.modalidadesPreferidas"
          label="Modalidades preferidas"
          hint="Tipo de compra que prefieres."
          placeholder="Licitación Pública"
          form={form}
        />
      </div>
    </div>
  );
}

/**
 * Solo lectura: el historial ya no se edita a mano, se calcula en el
 * servidor desde los movimientos reales con licitaciones (postulaciones y
 * adjudicaciones registradas en TenantLicitacion).
 */
function HistorialTab({ historial }: { historial: CompanyProfile["historial"] }) {
  const sinActividad = historial.postuladasTotales === 0;

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground -mt-1">
        Calculado automáticamente desde tus postulaciones y adjudicaciones registradas. No es
        editable.
      </p>

      {sinActividad ? (
        <EmptyState message="Aún no hay postulaciones registradas. Este historial se completará solo a medida que postules a licitaciones." />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <Stat value={historial.ganadas} label="Ganadas" />
            <Stat value={historial.perdidas} label="Perdidas" />
            <Stat value={historial.postuladasTotales} label="Postuladas totales" />
            <Stat value={`${Math.round(historial.tasaAdjudicacion * 100)}%`} label="Tasa de adjudicación" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Organismos frecuentes</Label>
              <div className="space-y-2 mt-2">
                {historial.organismosFrecuentes.length ? (
                  historial.organismosFrecuentes.map((o) => (
                    <div
                      key={o.nombre}
                      className="flex items-center justify-between text-sm rounded-lg border border-border px-3 py-2"
                    >
                      <span className="text-foreground truncate pr-2">{o.nombre}</span>
                      <span className="font-mono text-muted-foreground shrink-0">
                        {o.adjudicadas} adjudicadas
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">Sin adjudicaciones todavía.</p>
                )}
              </div>
            </div>

            <div>
              <Label>Categorías exitosas</Label>
              <div className="space-y-2 mt-2">
                {historial.categoriasExitosas.length ? (
                  historial.categoriasExitosas.map((c) => (
                    <div
                      key={c.nombre}
                      className="flex items-center justify-between text-sm rounded-lg border border-border px-3 py-2"
                    >
                      <span className="text-foreground truncate pr-2">{c.nombre}</span>
                      <span className="font-mono text-muted-foreground shrink-0">
                        {Math.round(c.tasa * 100)}%
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">Sin categorías exitosas todavía.</p>
                )}
              </div>
            </div>
          </div>

          {historial.adjudicacionesPorConfirmar.length > 0 && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-medium text-warning">
                <AlertCircle className="size-4 shrink-0" />
                {historial.adjudicacionesPorConfirmar.length} adjudicación
                {historial.adjudicacionesPorConfirmar.length === 1 ? "" : "es"} declarada
                {historial.adjudicacionesPorConfirmar.length === 1 ? "" : "s"} sin confirmar en
                Mercado Público
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Marcaste estas licitaciones como adjudicadas en tu pipeline, pero el dato oficial de
                Mercado Público todavía no lo respalda (puede tardar en publicarse, o valga la pena
                revisarlas).
              </p>
              <ul className="space-y-1">
                {historial.adjudicacionesPorConfirmar.map((a) => (
                  <li
                    key={a.licitacionCodigo}
                    className="flex items-center justify-between text-xs rounded border border-border px-2 py-1.5"
                  >
                    <span className="truncate pr-2">
                      {a.licitacionCodigo} · {a.organismo}
                    </span>
                    {a.confirmadaOc && (
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-success/15 text-success">
                        Con orden de compra
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
      <AlertCircle className="size-8 mb-2 opacity-50" />
      <p className="text-sm max-w-xs">{message}</p>
    </div>
  );
}

/**
 * Conecta el campo de chips (TagsField) con react-hook-form. Cuando
 * `remoteSearch` está presente, cada tecleo dispara (con debounce) una
 * búsqueda contra un catálogo real en vez de dejar el campo 100% libre.
 */
function ListField({
  name,
  label,
  hint,
  help,
  placeholder,
  form,
  remoteSearch,
}: {
  name: string;
  label: string;
  hint?: string;
  help?: string;
  placeholder?: string;
  form: ReturnType<typeof useForm<FormValues>>;
  remoteSearch?: (query: string) => Promise<string[]>;
}) {
  const value = (form.watch(name as never) as unknown as string[]) ?? [];
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleDraftChange = remoteSearch
    ? (draft: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (draft.trim().length < 2) {
          setSuggestions([]);
          return;
        }
        debounceRef.current = setTimeout(() => {
          remoteSearch(draft)
            .then(setSuggestions)
            .catch(() => setSuggestions([]));
        }, 250);
      }
    : undefined;

  return (
    <TagsField
      label={label}
      hint={hint}
      help={help}
      placeholder={placeholder}
      value={value}
      onChange={(next) =>
        form.setValue(name as never, next as never, { shouldDirty: true, shouldValidate: true })
      }
      suggestions={remoteSearch ? suggestions : undefined}
      onDraftChange={handleDraftChange}
    />
  );
}


function Stat({ value, label, large }: { value: string | number; label: string; large?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div
        className={cn(
          "font-display font-semibold text-foreground tabular-nums",
          large ? "text-2xl" : "text-lg",
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}
